import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Truck, Tag, Percent, X } from "lucide-react";

/**
 * Shared editor for an order's fee lines (+ Ongkir / + Biaya Lain) and discount
 * lines (+ Diskon, Rp or % of the order's product subtotal). Used by the Order
 * editor and by the Invoice page — both write to the same order fields.
 */
let _k = 0;
const key = () => `l${++_k}`;

export const newFeeLine = (name = "") => ({ _key: key(), name, amount: "" });
export const newDiscountLine = () => ({ _key: key(), name: "", type: "fixed", value: "" });

export function linesFromOrder(order) {
  return {
    feeLines: (order?.fee_lines || []).map((f) => ({ _key: key(), id: f.id, name: f.name || "", amount: String(f.amount ?? "") })),
    discountLines: (order?.discount_lines || []).map((d) => ({ _key: key(), id: d.id, name: d.name || "", type: d.type || "fixed", value: String(d.value ?? "") })),
  };
}

export function stripLineKeys(arr) { return (arr || []).map(({ _key, ...rest }) => rest); }

export function validateLines(feeLines, discountLines) {
  for (const f of feeLines) {
    if (!(f.name || "").trim()) return "Nama biaya wajib diisi";
    if (!(Number(f.amount) > 0)) return `Isi nominal untuk biaya "${f.name}"`;
  }
  for (const d of discountLines) {
    if (!(d.name || "").trim()) return "Nama diskon wajib diisi";
    if (!(Number(d.value) > 0)) return `Isi nilai diskon "${d.name}"`;
    if (d.type === "percent" && Number(d.value) > 100) return "Diskon persen maksimal 100%";
  }
  return null;
}

/** Client-side estimate (for confirmation prompts only — the server is the source of truth). */
export function estimateTotal(itemsSubtotal, feeLines, discountLines) {
  const sub = Number(itemsSubtotal) || 0;
  const fees = feeLines.reduce((s, f) => s + (Number(f.amount) || 0), 0);
  const disc = discountLines.reduce((s, d) => s + (d.type === "percent" ? Math.round(sub * (Number(d.value) || 0) / 100) : (Number(d.value) || 0)), 0);
  return Math.max(0, sub + fees - disc);
}

export function PricingLinesEditor({ feeLines, setFeeLines, discountLines, setDiscountLines, title = "Biaya & Diskon", testPrefix = "order", emptyHint }) {
  const addFee = (name) => setFeeLines((ls) => [...ls, newFeeLine(name)]);
  const updFee = (i, k, v) => setFeeLines((ls) => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const rmFee = (i) => setFeeLines((ls) => ls.filter((_, idx) => idx !== i));
  const addDisc = () => setDiscountLines((ls) => [...ls, newDiscountLine()]);
  const updDisc = (i, k, v) => setDiscountLines((ls) => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const rmDisc = (i) => setDiscountLines((ls) => ls.filter((_, idx) => idx !== i));

  return (
    <section className="space-y-3" data-testid={`${testPrefix}-lines-editor`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {title && <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>}
        <div className="flex items-center gap-1.5 flex-wrap">
          <Button type="button" variant="outline" size="sm" onClick={() => addFee("Ongkir")} data-testid={`${testPrefix}-add-ongkir`}><Truck className="h-3.5 w-3.5 mr-1" />Ongkir</Button>
          <Button type="button" variant="outline" size="sm" onClick={() => addFee("")} data-testid={`${testPrefix}-add-fee`}><Plus className="h-3.5 w-3.5 mr-1" />Biaya Lain</Button>
          <Button type="button" variant="outline" size="sm" onClick={addDisc} data-testid={`${testPrefix}-add-discount`}><Tag className="h-3.5 w-3.5 mr-1" />Diskon</Button>
        </div>
      </div>
      {feeLines.length === 0 && discountLines.length === 0 && (
        <p className="text-xs text-muted-foreground" data-testid={`${testPrefix}-lines-empty`}>
          {emptyHint || "Belum ada biaya/diskon. Ongkir, packing, asuransi atau diskon ditambahkan di sini dan tampil sebagai baris tersendiri di invoice."}
        </p>
      )}
      <div className="space-y-2">
        {feeLines.map((f, i) => (
          <div key={f._key} className="flex items-center gap-2" data-testid={`${testPrefix}-fee-line`}>
            <Badge variant="outline" className="text-[10px] shrink-0 border-primary text-primary w-14 justify-center">Biaya</Badge>
            <Input className="h-9 flex-1 min-w-0" placeholder="Nama biaya (Ongkir, Packing, Asuransi...)" value={f.name} onChange={(e) => updFee(i, "name", e.target.value)} data-testid={`${testPrefix}-fee-name`} />
            <Input className="h-9 w-28 sm:w-40" type="number" min="0" step="1000" inputMode="numeric" placeholder="Rp" value={f.amount} onChange={(e) => updFee(i, "amount", e.target.value)} data-testid={`${testPrefix}-fee-amount`} />
            <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-destructive shrink-0" onClick={() => rmFee(i)} data-testid={`${testPrefix}-fee-remove`}><X className="h-4 w-4" /></Button>
          </div>
        ))}
        {discountLines.map((d, i) => (
          <div key={d._key} className="flex items-center gap-2" data-testid={`${testPrefix}-discount-line`}>
            <Badge variant="outline" className="text-[10px] shrink-0 border-[hsl(var(--warning))] text-[hsl(var(--warning))] w-14 justify-center">Diskon</Badge>
            <Input className="h-9 flex-1 min-w-0" placeholder="Nama diskon (Diskon loyal, Promo IG...)" value={d.name} onChange={(e) => updDisc(i, "name", e.target.value)} data-testid={`${testPrefix}-discount-name`} />
            <Select value={d.type} onValueChange={(v) => updDisc(i, "type", v)}>
              <SelectTrigger className="h-9 w-20 shrink-0" data-testid={`${testPrefix}-discount-type`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fixed">Rp</SelectItem>
                <SelectItem value="percent">%</SelectItem>
              </SelectContent>
            </Select>
            <Input className="h-9 w-24 sm:w-32" type="number" min="0" step={d.type === "percent" ? "1" : "1000"} max={d.type === "percent" ? 100 : undefined} inputMode="numeric" placeholder={d.type === "percent" ? "%" : "Rp"} value={d.value} onChange={(e) => updDisc(i, "value", e.target.value)} data-testid={`${testPrefix}-discount-value`} />
            <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-destructive shrink-0" onClick={() => rmDisc(i)} data-testid={`${testPrefix}-discount-remove`}><X className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      {discountLines.some((d) => d.type === "percent") && (
        <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Percent className="h-3 w-3" />Diskon persen dihitung dari subtotal produk order ini (tidak termasuk biaya).</p>
      )}
    </section>
  );
}
